import { useEffect, useRef, useState, type ReactNode } from "react";

import type { Asset, SourceRef } from "../../../api/generated/model";
import { useGenerateImage } from "../../../platform/media/GenerateImageDialog";
import { pictureOf, type PickedImage } from "../../../platform/media/ImagePicker";
import { ReferencesDialog } from "../../../platform/media/ReferencesDialog";
import type { ImageRefs } from "../../../platform/media/ImageRefsSlot";
import { IMAGE_ONLY, useMediaUpload, type MediaUpload } from "../../../platform/media/media-upload";

/** Live's words (`music/constants.ts`: `MUSIC_TEXT`, `MUSIC_TOOLTIP.IMAGE_REF`, `MUSIC_ERROR`). */
const TITLE = "Select Image References";
const SUBTITLE = "Choose images to reference";
export const IMAGE_REF_TIP =
  "Optional images to use as inspiration for music generation. The model will create music that matches the mood and theme of the images.";
export const IMAGE_UPLOADING = "Wait for the images to finish uploading";

export interface MusicImages {
  /** The images chosen, in order, and the refs a request sends. */
  refs: SourceRef[];
  /** The "Image refs" slot's state. */
  slot: ImageRefs;
  /** Live's library dialog or its Generate image dialog, while one is open. */
  dialog: ReactNode;
  upload: MediaUpload;
  /** Live's model change: the images past the next model's most go (all, where it takes none). */
  keep: (most: number) => void;
}

/**
 * Live's Music "Image refs" (#219, `image-refs-field`, captures L01e and L01j): up to the model's
 * most, from the library in live's dialog, generated in live's Generate image dialog (#598), or
 * uploaded, several files at once up to the places left; each removable. At the most the tile
 * goes, as live's. Only a model that takes images shows the slot and is sent them.
 */
export function useMusicImages(teamId: string, brandId: string, max: number): MusicImages {
  const [images, setImages] = useState<PickedImage[]>([]);
  const [picking, setPicking] = useState(false);
  const running = useRef<AbortController | null>(null);
  useEffect(() => () => running.current?.abort(), []);
  // An image uploaded or generated joins the end, while there is room.
  const add = (asset: Asset) =>
    setImages((current) =>
      [
        ...current,
        {
          assetId: asset.id,
          url: pictureOf(asset),
          name: asset.name,
          width: asset.width,
          height: asset.height,
        },
      ].slice(0, max),
    );
  const generate = useGenerateImage({ teamId, brandId, onSaved: add });
  const upload = useMediaUpload({
    teamId,
    brandId,
    kinds: IMAGE_ONLY,
    onUploaded: add,
    onUploading: (controller) => {
      running.current = controller;
    },
  });
  const shown = images.slice(0, max);
  return {
    keep: (most) => setImages((current) => current.slice(0, most)),
    refs: shown.map((image) => ({ kind: "asset", asset_id: image.assetId })),
    slot: {
      images: shown,
      tagged: false,
      disabled: false,
      full: shown.length >= max,
      hideWhenFull: true,
      onFromLibrary: () => setPicking(true),
      onGenerate: generate.open,
      multiple: true,
      onUpload: (files) => void upload.sendAll(files, max - shown.length),
      uploading: upload.pending,
      onRemove: (assetId) => setImages((current) => current.filter((each) => each.assetId !== assetId)),
      wrap: (element) => element,
      limits: [IMAGE_REF_TIP],
    },
    dialog: picking ? (
      <ReferencesDialog
        teamId={teamId}
        brandId={brandId}
        kind="image"
        title={TITLE}
        subtitle={SUBTITLE}
        max={max}
        chosen={shown.map((image) => image.assetId)}
        onClose={() => setPicking(false)}
        onConfirm={(chosen) => setImages(chosen.slice(0, max))}
      />
    ) : (
      generate.dialog
    ),
    upload,
  };
}
